import React from 'react';

// Salt-and-pepper noise overlay for the contact page, with occasional glitch
// bursts: horizontal bands of dense noise torn sideways for a few frames.
// Rendered at reduced resolution and scaled up so the grain reads as pixels.

const PIXEL = 3;          // CSS px per noise pixel
const FRAME_SKIP = 4;     // ~15fps; noise that refreshes too fast reads as static fuzz
const DENSITY = 0.012;    // fraction of pixels that are salt or pepper
const BURST_EVERY = [9, 20];    // seconds between glitch bursts (min, max)
const BURST_FRAMES = 6;

class ContactNoise extends React.Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.frame = 0;
    this.burstLeft = 0;
    this.nextBurst = 2;
    this.bands = [];
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    this.last = null;
    const loop = (now) => {
      this.frame++;
      if (this.frame % FRAME_SKIP === 0) {
        const dt = this.last ? Math.min(0.1, (now - this.last) / 1000) : 0.016;
        this.last = now;
        this.step(dt);
        this.draw();
      }
      this.animationFrameId = requestAnimationFrame(loop);
    };
    this.animationFrameId = requestAnimationFrame(loop);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
  }

  handleResize() {
    const canvas = this.canvasRef.current;
    if (!canvas || !canvas.parentElement) return;
    const w = canvas.parentElement.clientWidth || window.innerWidth;
    const h = canvas.parentElement.clientHeight || window.innerHeight;
    canvas.width = Math.max(1, Math.floor(w / PIXEL));
    canvas.height = Math.max(1, Math.floor(h / PIXEL));
    this.image = null;
  }

  step(dt) {
    if (this.burstLeft > 0) {
      this.burstLeft--;
      return;
    }
    this.nextBurst -= dt * FRAME_SKIP;
    if (this.nextBurst <= 0) {
      this.nextBurst = BURST_EVERY[0] + Math.random() * (BURST_EVERY[1] - BURST_EVERY[0]);
      this.burstLeft = BURST_FRAMES;
      const canvas = this.canvasRef.current;
      const h = canvas ? canvas.height : 100;
      const count = 2 + Math.floor(Math.random() * 4);
      this.bands = Array.from({ length: count }, () => ({
        y: Math.floor(Math.random() * h),
        height: 1 + Math.floor(Math.random() * Math.max(2, h * 0.06)),
        shift: Math.floor((Math.random() - 0.5) * 40),
        density: 0.15 + Math.random() * 0.3
      }));
    }
  }

  draw() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    if (!this.image) this.image = ctx.createImageData(w, h);
    const data = this.image.data;
    data.fill(0);

    // Base salt and pepper
    const n = Math.floor(w * h * DENSITY);
    for (let i = 0; i < n; i++) {
      const idx = Math.floor(Math.random() * w * h) * 4;
      const v = Math.random() < 0.5 ? 255 : 0;
      data[idx] = v; data[idx + 1] = v; data[idx + 2] = v;
      data[idx + 3] = v === 255 ? 90 + Math.random() * 70 : 150;
    }

    // Glitch bands: dense noise rows, torn sideways
    if (this.burstLeft > 0) {
      for (const b of this.bands) {
        for (let y = b.y; y < Math.min(h, b.y + b.height); y++) {
          for (let x = 0; x < w; x++) {
            if (Math.random() > b.density) continue;
            const sx = ((x + b.shift) % w + w) % w;
            const idx = (y * w + sx) * 4;
            const v = Math.random() < 0.6 ? 255 : 0;
            data[idx] = v; data[idx + 1] = v; data[idx + 2] = v; data[idx + 3] = 230;
          }
        }
      }
    }

    ctx.putImageData(this.image, 0, 0);
  }

  render() {
    return <canvas ref={this.canvasRef} className="contact-noise" aria-hidden="true" />;
  }
}

export default ContactNoise;
